import type { Language } from "@/lib/i18n";

export type SeoPage =
  | "home"
  | "login"
  | "signup"
  | "privacy"
  | "terms"
  | "legal"
  | "contact"
  | "dashboard"
  | "how-it-works"
  | "pricing"
  | "about"
  | "data-deletion"
  | "blog"
  | "google-data-usage";

export const pagePathMap: Record<SeoPage, string> = {
  home: "/",
  login: "/login",
  signup: "/signup",
  privacy: "/privacy-policy",
  terms: "/terms",
  legal: "/legal-notice",
  contact: "/contact",
  dashboard: "/dashboard",
  "how-it-works": "/how-it-works",
  pricing: "/pricing",
  about: "/about",
  "data-deletion": "/data-deletion",
  blog: "/blog",
  "google-data-usage": "/google-data-usage",
};

const localizedSeoPages = new Set<SeoPage>([
  "home",
  "privacy",
  "terms",
  "legal",
  "contact",
  "how-it-works",
  "pricing",
  "about",
  "data-deletion",
  "blog",
  "google-data-usage",
]);

const localizedPublicPrefixes = [
  "/",
  "/privacy-policy",
  "/terms",
  "/legal-notice",
  "/data-deletion",
  "/contact",
  "/how-it-works",
  "/pricing",
  "/about",
  "/google-data-usage",
  "/blog",
] as const;

export function stripLocalePrefix(pathname: string): string {
  if (pathname === "/en") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3);
  return pathname || "/";
}

export function isLocalizedPublicPath(pathname: string): boolean {
  const normalized = stripLocalePrefix(pathname);

  return localizedPublicPrefixes.some((prefix) => {
    if (prefix === "/") return normalized === "/";
    return normalized === prefix || normalized.startsWith(`${prefix}/`);
  });
}

export function localizePublicPath(inputPath: string, language: Language): string {
  const match = inputPath.match(/^([^?#]*)(.*)$/);
  const rawPath = match?.[1] || "/";
  const suffix = match?.[2] || "";
  const normalizedPath = rawPath || "/";
  const basePath = stripLocalePrefix(normalizedPath);

  if (!isLocalizedPublicPath(basePath)) {
    return `${normalizedPath}${suffix}`;
  }

  const localizedPath = language === "en"
    ? basePath === "/"
      ? "/en"
      : `/en${basePath}`
    : basePath;

  return `${localizedPath}${suffix}`;
}

export function isLocalizedSeoPage(page: SeoPage): boolean {
  return localizedSeoPages.has(page);
}

export function getLocalizedPagePath(page: SeoPage, language: Language): string {
  const basePath = pagePathMap[page];
  return isLocalizedSeoPage(page) ? localizePublicPath(basePath, language) : basePath;
}

export const prerenderStaticPaths = [
  "/",
  "/en",
  "/how-it-works",
  "/en/how-it-works",
  "/pricing",
  "/en/pricing",
  "/about",
  "/en/about",
  "/blog",
  "/en/blog",
  "/contact",
  "/en/contact",
  "/privacy-policy",
  "/en/privacy-policy",
  "/terms",
  "/en/terms",
  "/legal-notice",
  "/en/legal-notice",
  "/data-deletion",
  "/en/data-deletion",
  "/google-data-usage",
  "/en/google-data-usage",
] as const;
